import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../../api/axios";

export default function PaymentCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const squadReference = searchParams.get("transaction_ref") || searchParams.get("reference");
  const tx_ref = searchParams.get("tx_ref") || searchParams.get("txref") || squadReference || null;
  const paymentProvider = searchParams.get("provider") === "squad" || squadReference ? "squad" : "flutterwave";
  const [status, setStatus] = useState("checking");
  const [transaction, setTransaction] = useState(null);
  const [message, setMessage] = useState("Checking transaction...");

  useEffect(() => {
    if (!tx_ref) {
      setStatus("failed");
      setMessage("No transaction reference found.");
      return;
    }

    let cancelled = false;
    let redirectTimer = null;

    async function verifyOnce() {
      try {
        const isSquad = paymentProvider === "squad";
        const { data } = await api.get(`/payments/${isSquad ? "squad" : "flutterwave"}/verify`, {
          params: isSquad ? { transaction_ref: tx_ref } : { tx_ref },
        });

        setTransaction(data.transaction || null);
        return data.status || "pending";
      } catch (err) {
        const backendMessage = err?.response?.data?.message || err?.message || "verify failed";
        setMessage(backendMessage);

        return "pending";
      }
    }

    async function pollStatus() {
      const maxAttempts = 12;
      let attempts = 0;

      while (!cancelled && attempts < maxAttempts) {
        const currentStatus = await verifyOnce();

        if (currentStatus === "success") {
          setStatus("success");
          setMessage("Payment verified — your wallet has been credited.");
          redirectTimer = window.setTimeout(() => navigate("/dashboard", { replace: true }), 1500);
          return;
        }

        if (currentStatus === "failed") {
          setStatus("failed");
          setMessage("Payment failed or could not be verified.");
          return;
        }

        setStatus("pending");
        setMessage("Your payment is pending. Waiting for confirmation...");
        attempts += 1;

        if (attempts >= maxAttempts) break;
        await new Promise((resolve) => window.setTimeout(resolve, 3000));
      }
    }

    pollStatus();

    return () => {
      cancelled = true;
      if (redirectTimer) {
        window.clearTimeout(redirectTimer);
      }
    };
  }, [tx_ref, paymentProvider, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="bg-white p-6 rounded shadow w-full max-w-lg text-center">
        <h2 className="text-xl font-semibold mb-4">Payment Status</h2>

        {!tx_ref && <p className="text-red-500">No transaction reference found.</p>}

        {tx_ref && status === "checking" && <p>{message}</p>}
        {tx_ref && status === "pending" && (
          <>
            <p className="text-yellow-600">{message}</p>
            <p className="text-sm text-gray-500 mt-2">This usually completes in a few seconds.</p>
          </>
        )}
        {tx_ref && status === "success" && (
          <>
            <p className="text-green-600 font-semibold">{message}</p>
            <div className="mt-4 text-left text-sm bg-gray-50 p-3 rounded">
              <div>Reference: {transaction?.reference}</div>
              <div>Amount: {transaction?.amount}</div>
            </div>
          </>
        )}
        {tx_ref && status === "failed" && (
          <>
            <p className="text-red-600 font-semibold">{message}</p>
            <p className="text-sm text-gray-500 mt-2">If your card was charged, contact support with reference {tx_ref}.</p>
          </>
        )}

        <div className="mt-6">
          <button onClick={() => navigate("/dashboard", { replace: true })} className="px-4 py-2 bg-primary text-white rounded">
            Go to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}